import React, { useState, useMemo } from 'react';
import { SectionCard } from '../sections/SectionCard';
import { PANEL_SPACING } from '../../constants/spacing';
import { ChevronRight, ChevronDown, BadgeCheck, ExternalLink } from 'lucide-react';
import { getRiskLevel, RISK_SCORING } from '@shared/utils/risk';
import { cn } from '@shared/lib/utils';
import type { RiskCheck, RiskCheckStatus } from '@shared/types/risk.types';

/** Per-status styling for risk check badges */
const CHECK_STATUS_STYLES: Record<RiskCheckStatus, { bg: string; border: string; text: string }> = {
  error:   { bg: 'bg-status-error-bg',   border: 'border-status-error-border',   text: 'text-status-error-text' },
  warning: { bg: 'bg-status-warning-bg', border: 'border-status-warning-border', text: 'text-status-warning-text' },
  success: { bg: 'bg-status-success-bg', border: 'border-status-success-border', text: 'text-status-success-text' },
};

/** Map risk level → Tailwind text color class for the score number */
const SCORE_TEXT_CLASS: Record<string, string> = {
  low: 'text-success',
  medium: 'text-warning',
  high: 'text-destructive',
  critical: 'text-destructive',
};

/** Map risk level → Tailwind bg class for bar segments */
const BAR_COLOR_CLASS: Record<string, string> = {
  low: 'bg-success',
  medium: 'bg-warning',
  high: 'bg-destructive',
  critical: 'bg-destructive',
};

// ── RiskMeter ─────────────────────────────────────────────────────────────

export interface RiskMeterProps {
  totalScore: number;
  maxScore: number;
  className?: string;
}

export const RiskMeter = React.memo(({ totalScore, maxScore, className }: RiskMeterProps) => {
  const level = getRiskLevel(totalScore);
  const scoreTextClass = SCORE_TEXT_CLASS[level];
  const barColorClass = BAR_COLOR_CLASS[level];
  const activeSegments = Math.round((totalScore / maxScore) * RISK_SCORING.SEGMENTS_COUNT);

  return (
    <div className={cn('flex flex-col justify-center items-start p-6 gap-2 bg-muted rounded-xl shrink-0', className)}>
      {/* Score display */}
      <div className="flex items-baseline gap-1">
        <span className={cn('font-semibold text-5xl leading-tight tracking-tight', scoreTextClass)}>
          {totalScore}
        </span>
        <span className="text-base text-muted-foreground">/ {maxScore}</span>
      </div>

      {/* Risk level label */}
      <div className="flex items-center gap-0.5">
        <BadgeCheck className="size-3.5 text-muted-foreground" strokeWidth={1} />
        <span className="text-xs uppercase text-muted-foreground tracking-tight">
          {level}
        </span>
      </div>

      {/* Bar segments */}
      <div
        role="progressbar"
        aria-valuenow={totalScore}
        aria-valuemax={maxScore}
        aria-label={`Risk score: ${totalScore} out of ${maxScore}`}
        className="flex flex-row items-center gap-[2.5px]"
      >
        {Array.from({ length: RISK_SCORING.SEGMENTS_COUNT }, (_, i) => (
          <div
            key={i}
            className={cn(
              'rounded-full shrink-0 h-14 w-[3px]',
              i < activeSegments ? barColorClass : 'bg-border',
            )}
          />
        ))}
      </div>
    </div>
  );
});

RiskMeter.displayName = 'RiskMeter';

// ── RiskCheckItem ─────────────────────────────────────────────────────────

interface RiskCheckItemProps {
  check: RiskCheck;
  isExpanded: boolean;
  onToggle: () => void;
  onRuleClick?: (ruleId: string) => void;
}

const RiskCheckItem = React.memo(({ check, isExpanded, onToggle, onRuleClick }: RiskCheckItemProps) => {
  const styles = CHECK_STATUS_STYLES[check.status];

  return (
    <div className="flex flex-col">
      <div className="flex flex-row items-center gap-3">
        {/* Colored badge */}
        <div
          className={cn(
            'w-6 h-6 rounded-md flex items-center justify-center shrink-0 border',
            styles.bg, styles.border,
          )}
        >
          <span
            className={cn(
              'text-xs leading-4 tracking-tight uppercase',
              styles.text,
            )}
          >
            {check.score}
          </span>
        </div>

        {/* Title button with chevron */}
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center py-2 gap-1.5 bg-transparent border-0 cursor-pointer"
        >
          <span className="font-medium text-sm leading-5 tracking-tight text-foreground whitespace-nowrap">
            {check.title}
          </span>
          {isExpanded ? (
            <ChevronDown className="size-4 text-foreground shrink-0" strokeWidth={1.33} />
          ) : (
            <ChevronRight className="size-4 text-foreground shrink-0" strokeWidth={1.33} />
          )}
        </button>
      </div>

      {/* Expandable details */}
      {isExpanded && (
        <div className="ml-9 mt-1 flex flex-col gap-2">
          <p className="text-xs leading-4 text-muted-foreground m-0">
            {check.description}
          </p>
          {check.ruleId && onRuleClick && (
            <button
              type="button"
              onClick={() => onRuleClick(check.ruleId!)}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors bg-transparent border-0 cursor-pointer p-0 w-fit"
            >
              View rule details
              <ExternalLink className="size-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
});

RiskCheckItem.displayName = 'RiskCheckItem';

// ── RiskChecksList ────────────────────────────────────────────────────────

interface RiskChecksListProps {
  riskChecks: RiskCheck[];
  onRuleClick?: (ruleId: string) => void;
}

const RiskChecksList = React.memo(({ riskChecks, onRuleClick }: RiskChecksListProps) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  return (
    <div className="flex flex-col items-start gap-3">
      {riskChecks.map((check, index) => (
        <RiskCheckItem
          key={`${check.title}-${index}`}
          check={check}
          isExpanded={expandedIndex === index}
          onToggle={() => setExpandedIndex(prev => prev === index ? null : index)}
          onRuleClick={onRuleClick}
        />
      ))}
    </div>
  );
});

RiskChecksList.displayName = 'RiskChecksList';

// ── Score calculation ────────────────────────────────────────────────────

/** Sum scores of error and warning checks, capped at maxScore. */
const defaultCalculateScore = (checks: RiskCheck[], maxScore: number): number => {
  if (!checks?.length) return 0;
  const total = checks.reduce(
    (sum, check) => (check.status !== 'success' ? sum + check.score : sum),
    0,
  );
  return Math.min(total, maxScore);
};

// ── Props & Component ─────────────────────────────────────────────────────

export interface RiskScoreProps {
  riskChecks: RiskCheck[];
  /**
   * Maximum risk score for the meter display. Defaults to 50.
   * Risk level thresholds in RISK_THRESHOLDS are absolute values calibrated
   * for maxScore=50. Changing this without a custom calculateScore may
   * produce misleading risk level labels.
   */
  maxScore?: number;
  calculateScore?: (riskChecks: RiskCheck[]) => number;
  isDetailView?: boolean;
  /** When true, renders only the meter + checks without a SectionCard wrapper */
  standalone?: boolean;
  /** Callback when a rule link is clicked. When omitted, rule links are hidden. */
  onRuleClick?: (ruleId: string) => void;
  className?: string;
}

export const RiskScore = React.memo<RiskScoreProps>(({
  riskChecks,
  isDetailView = false,
  maxScore = 50,
  calculateScore,
  standalone = false,
  onRuleClick,
  className,
}) => {
  const totalScore = useMemo(() => {
    return calculateScore ? calculateScore(riskChecks) : defaultCalculateScore(riskChecks, maxScore);
  }, [calculateScore, riskChecks, maxScore]);

  const content = (
    <div className={cn('flex w-full', standalone && className)}>
      <RiskMeter totalScore={totalScore} maxScore={maxScore} />
      <div className={`${PANEL_SPACING.CONTENT_RIGHT_WIDTH} ml-auto`}>
        <RiskChecksList riskChecks={riskChecks} onRuleClick={onRuleClick} />
      </div>
    </div>
  );

  if (standalone) {
    return content;
  }

  return (
    <SectionCard
      title="Risk score"
      layout={isDetailView ? 'detail' : 'down'}
      className={className}
    >
      {content}
    </SectionCard>
  );
});

RiskScore.displayName = 'RiskScore';
